import { IAssetMetadata } from "../..";
import { MediaType } from "../../../types";
import { PixabayVideoItem, PixabayVideoResolution } from "../types";
import { BasePixabayAsset, deriveVideoName } from "./base";

const VIDEO_QUALITY_ORDER: (keyof PixabayVideoItem['videos'])[] = ['large', 'medium', 'small', 'tiny'];

function getBestResolution(videos: PixabayVideoItem['videos']): PixabayVideoResolution | undefined {
  for (const key of VIDEO_QUALITY_ORDER) {
    if (videos[key]) return videos[key];
  }
  return undefined;
}

export class VideoAsset extends BasePixabayAsset {
  private item: PixabayVideoItem;

  constructor(item: PixabayVideoItem) {
    super(item.pageURL, item.tags, item.user, item.likes, item.downloads);
    this.item = item;
  }

  getName(): string {
    return deriveVideoName(this.item.pageURL);
  }

  getMediaType(): MediaType {
    return MediaType.Video;
  }

  getImageUrlForCard(): string {
    return this.item.videos.tiny?.thumbnail ?? this.item.videos.small?.thumbnail ?? '';
  }

  getPreviewUrl(): string {
    return getBestResolution(this.item.videos)?.url ?? '';
  }

  getDownloadUrl(): string {
    return getBestResolution(this.item.videos)?.url ?? '';
  }

  async downloadFile(): Promise<{ blob: Blob; filename: string }> {
    const url = this.getDownloadUrl();
    if (!url) throw new Error('No downloadable video URL available');
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to download Pixabay video: ${response.statusText}`);
    const blob = await response.blob();
    const ext = url.split('.').pop()?.split('?')[0] ?? 'mp4';
    return { blob, filename: `${this.getName()}.${ext}` };
  }

  getMetadata(): IAssetMetadata {
    const resolutions = VIDEO_QUALITY_ORDER.reduce((acc, key) => {
      const res = this.item.videos[key];
      if (res) acc[key] = `${res.width} x ${res.height}`;
      return acc;
    }, {} as Record<string, string>);

    return {
      status: 'ready',
      date: '',
      size: getBestResolution(this.item.videos)?.size ?? 0,
      licence: 'Pixabay-licence',
      licenceUrl: 'https://pixabay.com/service/license-summary/',
      author: [this.item.user],
      tags: this.tags,
      categories: [],
      downloads: this.item.downloads,
      views: this.item.views,
      type: this.item.type,
      ...resolutions,
    };
  }
}
